import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { SITE_URL } from "@/lib/seo";
import { submitEnquiry } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";
import { toast } from "sonner";

const easing = [0.22, 0.61, 0.36, 1];

const initial = {
  project_type: "",
  name: "",
  email: "",
  business_name: "",
  industry: "",
  website_url: "",
  description: "",
  goals: "",
  budget: "",
  timeline: "",
};

const stepsDef = [
  { key: "intro", fields: [] },
  { key: "project-type", label: "Are you launching a new site, or refreshing an existing one?", fields: ["project_type"] },
  { key: "name", label: "What should we call you?", fields: ["name"] },
  { key: "email-biz", label: "Where can we reach you?", fields: ["email", "business_name"] },
  { key: "industry-url", label: "Tell us about your business.", fields: ["industry", "website_url"] },
  { key: "describe", label: "Describe your business in your own words.", fields: ["description"] },
  { key: "goals", label: "What would you like this project to achieve?", fields: ["goals"] },
  { key: "budget-timeline", label: "Budget and timeline?", fields: ["budget", "timeline"] },
  { key: "review", label: "A final look before we send.", fields: [] },
];

const required = ["project_type", "name", "email", "business_name", "industry", "description", "goals", "budget", "timeline"];

const projectTypeOptions = [
  { value: "new", label: "A new website", sub: "Launching online for the first time." },
  { value: "redesign", label: "Redesign an existing site", sub: "Refresh what is already there." },
];

const budgetOptions = ["£99 a month (LAK Launch)", "Custom scope, larger build", "Still deciding"];
const timelineOptions = ["As soon as possible", "Within 1 month", "1-3 months", "Exploring"];

function Field({ id, label, type = "text", value, onChange, placeholder, optional }) {
  return (
    <label className="block">
      <span className="eyebrow text-lak-soft">{label}{!optional && <span className="text-lak-sage"> *</span>}</span>
      {type === "textarea" ? (
        <textarea
          data-testid={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={5}
          placeholder={placeholder}
          className="mt-3 w-full bg-transparent border-0 border-b border-black/20 focus:border-lak-sage focus:outline-none py-3 text-2xl sm:text-3xl font-serif font-light tracking-tight placeholder:text-lak-soft/40"
        />
      ) : (
        <input
          data-testid={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="mt-3 w-full bg-transparent border-0 border-b border-black/20 focus:border-lak-sage focus:outline-none py-3 text-2xl sm:text-3xl font-serif font-light tracking-tight placeholder:text-lak-soft/40"
        />
      )}
    </label>
  );
}

function ChoiceField({ id, label, value, onChange, options }) {
  return (
    <div>
      <span className="eyebrow text-lak-soft">{label}<span className="text-lak-sage"> *</span></span>
      <div className="mt-4 flex flex-wrap gap-3">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            data-testid={`${id}-${opt.replace(/\s+/g, "-").toLowerCase().slice(0, 24)}`}
            onClick={() => onChange(opt)}
            className={`px-5 py-3 text-sm tracking-wide border transition-all ${value === opt ? "bg-lak-text text-lak-bg border-lak-text" : "bg-transparent border-black/20 text-lak-text hover:border-lak-text"}`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [stepIdx, setStepIdx] = useState(0);
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Pre-select project type from ?type=new|redesign and skip the intro step
  useEffect(() => {
    const t = searchParams.get("type");
    if (t === "new" || t === "redesign") {
      setData((d) => ({ ...d, project_type: t }));
      setStepIdx(2); // jump past intro + project-type
    }
  }, [searchParams]);

  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));
  const step = stepsDef[stepIdx];
  const isLast = stepIdx === stepsDef.length - 1;
  const isFirst = stepIdx === 0;

  const canNext = useMemo(() => {
    return step.fields.every((f) => {
      if (!required.includes(f)) return true;
      const v = (data[f] || "").trim();
      if (f === "email") return /.+@.+\..+/.test(v);
      return v.length > 0;
    });
  }, [step, data]);

  const next = () => {
    if (!canNext) {
      toast.error("Please complete this step before continuing.");
      return;
    }
    setStepIdx((i) => Math.min(i + 1, stepsDef.length - 1));
  };
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const submit = async () => {
    // validate everything required
    for (const k of required) {
      if (!(data[k] || "").trim()) {
        toast.error("A few fields are still missing, please pop back and complete them.");
        return;
      }
    }
    setSubmitting(true);
    try {
      await submitEnquiry(data);
      setSuccess(true);
    } catch (e) {
      console.error(e);
      toast.error("We couldn't send that enquiry, please try once more.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-32 sm:pt-40 bg-lak-bg min-h-screen">
      <Seo
        title="Enquire · Start Your Project with LAK Atelier"
        description="Tell us about your project. A short, considered enquiry, read by the atelier within one working day. Your website built free, with a £99 monthly retainer."
        keywords="contact web designer UK, hire boutique web design studio, start a website project, web design enquiry"
        breadcrumbs={[
          { name: "Home", url: `${SITE_URL}/` },
          { name: "Enquire", url: `${SITE_URL}/contact` },
        ]}
      />
      <div className="lak-section max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 pb-32">
        <div className="grid grid-cols-12 gap-10">
          <aside className="col-span-12 lg:col-span-4">
            <Reveal>
              <span className="eyebrow">Enquire</span>
              <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02]">
                Tell us about your <em className="italic text-lak-sage">project.</em>
              </h1>
              <p className="mt-8 text-lak-soft max-w-md">
                A short, considered enquiry, no forms with twenty fields, no chatbot pop-ups.
                We read every reply ourselves.
              </p>
              <div className="mt-12 space-y-3 text-sm">
                <p className="eyebrow">What happens next</p>
                <p className="text-lak-soft">01 · We read your enquiry within one working day.</p>
                <p className="text-lak-soft">02 · We propose a 20-minute discovery call.</p>
                <p className="text-lak-soft">03 · If we&rsquo;re a fit, we begin.</p>
              </div>
            </Reveal>
          </aside>

          <section className="lak-section col-span-12 lg:col-span-8 lg:pl-10 lg:border-l border-black/10">
            <div data-testid={CONTACT.form} className="min-h-[420px]">
              <div className="flex items-center justify-between mb-10">
                <span className="eyebrow">Step {Math.min(stepIdx, stepsDef.length - 1) + 1} / {stepsDef.length}</span>
                <div className="flex-1 mx-6 h-px bg-black/10 relative overflow-hidden">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-lak-sage"
                    initial={false}
                    animate={{ width: `${((stepIdx + (success ? 1 : 0)) / stepsDef.length) * 100}%` }}
                    transition={{ duration: 0.6, ease: easing }}
                  />
                </div>
              </div>

              <AnimatePresence mode="wait">
                {success ? (
                  <motion.div
                    key="success"
                    data-testid={CONTACT.success}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.8, ease: easing }}
                    className="py-16"
                  >
                    <motion.div
                      initial={{ scale: 0.6, rotate: -10, opacity: 0 }}
                      animate={{ scale: 1, rotate: 0, opacity: 1 }}
                      transition={{ duration: 1, ease: easing, delay: 0.1 }}
                      className="w-20 h-20 rounded-full bg-lak-sage flex items-center justify-center"
                    >
                      <Check className="text-lak-bg" size={32} strokeWidth={1.25} />
                    </motion.div>
                    <h2 className="mt-12 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02]">
                      Thank you. <em className="italic text-lak-sage">We&rsquo;ve received it.</em>
                    </h2>
                    <p className="mt-8 text-lak-soft text-lg max-w-xl">
                      We&rsquo;ve received your enquiry and will be in touch shortly. In the meantime,
                      have a look at the featured project, it&rsquo;s a small taste of how we work.
                    </p>
                    <a href="/work/petal-and-paw" className="mt-12 inline-flex items-center gap-3 px-8 py-4 btn-outline text-[11px] tracking-[0.28em] uppercase">
                      View Featured Project
                    </a>
                  </motion.div>
                ) : (
                  <motion.div
                    key={step.key}
                    data-testid={CONTACT.step(stepIdx)}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ duration: 0.6, ease: easing }}
                    className="py-4 space-y-10"
                  >
                    {step.key === "intro" && (
                      <div>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
                          Let&rsquo;s get started by asking you <em className="italic text-lak-sage">a few questions.</em>
                        </h2>
                        <p className="mt-6 text-lak-soft text-lg max-w-xl">
                          Nine light steps. About two minutes. We&rsquo;ll read every answer ourselves.
                        </p>
                      </div>
                    )}
                    {step.key === "project-type" && (
                      <div>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
                          Are you launching a new site, <em className="italic text-lak-sage">or refreshing an existing one?</em>
                        </h2>
                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {projectTypeOptions.map((opt) => {
                            const active = data.project_type === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => set("project_type")(opt.value)}
                                data-testid={`field-project-type-${opt.value}`}
                                className={`text-left p-7 border transition-all ${active ? "bg-lak-text text-lak-bg border-lak-text" : "bg-transparent border-black/20 hover:border-lak-text"}`}
                              >
                                <span className="eyebrow opacity-80">Option</span>
                                <p className="mt-3 font-serif italic text-2xl tracking-tight leading-snug">{opt.label}</p>
                                <p className={`mt-2 text-sm ${active ? "text-lak-bg/80" : "text-lak-soft"}`}>{opt.sub}</p>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {step.key === "name" && (
                      <Field id={CONTACT.field.name} label="Your name" value={data.name} onChange={set("name")} placeholder="e.g. Laura" />
                    )}
                    {step.key === "email-biz" && (
                      <div className="space-y-10">
                        <Field id={CONTACT.field.email} type="email" label="Your email" value={data.email} onChange={set("email")} placeholder="hello@yourbrand.com" />
                        <Field id={CONTACT.field.business} label="Business name" value={data.business_name} onChange={set("business_name")} placeholder="Atelier Maison" />
                      </div>
                    )}
                    {step.key === "industry-url" && (
                      <div className="space-y-10">
                        <Field id={CONTACT.field.industry} label="Industry" value={data.industry} onChange={set("industry")} placeholder="Florist · Wedding · Wellness" />
                        <Field id={CONTACT.field.website} label="Current website (optional)" value={data.website_url} onChange={set("website_url")} placeholder="www.yourbrand.com" optional />
                      </div>
                    )}
                    {step.key === "describe" && (
                      <Field id={CONTACT.field.description} type="textarea" label="Describe your business" value={data.description} onChange={set("description")} placeholder="A pet-safe florist based in London creating gentle, considered arrangements…" />
                    )}
                    {step.key === "goals" && (
                      <Field id={CONTACT.field.goals} type="textarea" label="Project goals" value={data.goals} onChange={set("goals")} placeholder="A website that finally looks like our brand and helps us turn visitors into bookings." />
                    )}
                    {step.key === "budget-timeline" && (
                      <div className="space-y-10">
                        <ChoiceField id={CONTACT.field.budget} label="Budget" value={data.budget} onChange={set("budget")} options={budgetOptions} />
                        <ChoiceField id={CONTACT.field.timeline} label="Timeline" value={data.timeline} onChange={set("timeline")} options={timelineOptions} />
                      </div>
                    )}
                    {step.key === "review" && (
                      <div className="space-y-8">
                        <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">A final look.</h2>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
                          {[
                            ["Project", data.project_type === "new" ? "New website" : data.project_type === "redesign" ? "Redesign of existing site" : ""],
                            ["Name", data.name], ["Email", data.email],
                            ["Business", data.business_name], ["Industry", data.industry],
                            ["Website", data.website_url || ""], ["Budget", data.budget],
                            ["Timeline", data.timeline],
                          ].map(([k, v]) => (
                            <div key={k}>
                              <dt className="eyebrow">{k}</dt>
                              <dd className="mt-2 font-serif text-xl italic font-light">{v || ""}</dd>
                            </div>
                          ))}
                        </dl>
                        <div>
                          <p className="eyebrow">Description</p>
                          <p className="mt-2 text-lak-soft">{data.description}</p>
                        </div>
                        <div>
                          <p className="eyebrow">Goals</p>
                          <p className="mt-2 text-lak-soft">{data.goals}</p>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {!success && (
                <div className="mt-12 flex flex-col-reverse sm:flex-row items-center sm:justify-between gap-4">
                  <button
                    type="button"
                    onClick={back}
                    disabled={isFirst}
                    data-testid={CONTACT.back}
                    className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-lak-soft disabled:opacity-30 hover:text-lak-text transition-colors py-2"
                  >
                    <ArrowLeft size={14} strokeWidth={1.25} /> Back
                  </button>

                  {!isLast ? (
                    <button
                      type="button"
                      onClick={next}
                      data-testid={CONTACT.next}
                      className="inline-flex items-center justify-center gap-3 w-full sm:w-auto px-8 py-4 btn-sage text-[11px] tracking-[0.28em] uppercase"
                    >
                      Continue <ArrowRight size={14} strokeWidth={1.25} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={submit}
                      disabled={submitting}
                      data-testid={CONTACT.submit}
                      className="inline-flex items-center justify-center gap-3 w-full sm:w-auto px-10 py-4 btn-sage text-[11px] tracking-[0.28em] uppercase disabled:opacity-60"
                    >
                      {submitting ? "Sending..." : "Send Enquiry"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
